<template>
    <div class="body store-app" :class="{ 'store-layout-large': storeLayout === 'large' }">
        <Navbar 
        :showBackButton="true" 
        @back="goBack"
        />
        

        <!-- Shopping Cart Component -->
        <ShoppingCart 
            ref="shoppingCart"
            @cart-updated="handleCartUpdate"
            @checkout="handleCheckout"
        />

        <!-- Store Header -->
        <div class="store-header">
            <div class="store-title">
                <h1>{{ $t('sales.kalaStore.store.title') }} <p> </p> <span v-if="companyName"> {{ companyName }} </span></h1>
                <!-- <p>{{ $t('sales.kalaStore.store.subtitle') }}</p> -->
            </div>
            
            <div class="store-actions">
                <button class="filter-btn" @click="toggleFilterPanel">
                    {{ $t('sales.kalaStore.store.filterButton') }}
                </button>

                <div class="search-container">
                    <input 
                        type="text" 
                        v-model="searchQuery"
                        @input="handleSearch"
                        :placeholder="$t('sales.kalaStore.store.searchPlaceholder')"
                        class="search-input"
                    />
                    <span class="search-icon">🔍</span>
                </div>
            </div>
        </div>

        <!-- Filter Panel -->
        <div v-if="showFilterPanel" class="filter-panel">
            <div class="filter-header">
                <h4>{{ $t('sales.kalaStore.filter.title') }}</h4>
                <button class="close-filter" @click="closeFilterPanel">×</button>
            </div>
            
            <div class="filter-content">
                <div class="filter-section">
                    <label>{{ $t('sales.kalaStore.filter.priceRange') }}:</label>
                    <div class="price-range">
                        <input type="number" v-model="filters.minPrice" :placeholder="$t('sales.kalaStore.filter.minPrice')" class="price-input">
                        <span style="margin-right: 5px;">{{ $t('sales.kalaStore.filter.to') }}</span>
                        <input type="number" v-model="filters.maxPrice" :placeholder="$t('sales.kalaStore.filter.maxPrice')" class="price-input">
                    </div>
                </div>
                
                <div class="filter-section">
                    <label>{{ $t('sales.kalaStore.filter.category') }}:</label>
                    <select v-model="filters.category" class="category-select">
                        <option value="">{{ $t('sales.kalaStore.filter.allCategories') }}</option>
                        <option v-for="category in categories" :key="category.SubCode" :value="category.MainCode">
                            {{ category.Name }}
                        </option>
                    </select>
                </div>
                
                <div class="filter-section">
                    <label class="checkbox-label">
                        <input type="checkbox" v-model="filters.inStockOnly">
                        {{ $t('sales.kalaStore.filter.inStockOnly') }}
                    </label>
                </div>
                
                <div class="filter-actions">
                    <button class="apply-filters" @click="applyFilters">
                        {{ $t('sales.kalaStore.filter.applyFilters') }}
                    </button>
                    <button class="reset-filters" @click="resetFilters">
                        {{ $t('sales.kalaStore.filter.resetFilters') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Categories Section - Horizontal Scroll -->
        <div id="categories-section1" class="categories-section">
            <div class="section-header-cat">
                <h3>{{ $t('sales.kalaStore.categories.title') }}</h3>
                <button class="view-all-categories" @click="openCategoryModal">
                    {{ $t('sales.kalaStore.categories.viewAll') }}
                    <span class="arrow-icon">›</span>
                </button>
            </div>
            <div class="categories-scroll">
                <div 
                    v-for="(category, categoryIndex) in categories" 
                    :key="category.SubCode"
                    class="category-item"
                    @click="goToCategoryPage(category)"
                    :class="{ active: activeCategory === category.MainCode }"
                >
                    <div class="category-icon-wrapper">
                        <img
                            v-if="category.Image1"
                            :src="getImageSrc(category.Image1)"
                            :alt="category.Name"
                            class="category-icon-image"
                            @error="handleCategoryImageError"
                        />
                        <img v-else :src="getCategoryFallbackIcon(categoryIndex)" :alt="category.Name" class="category-icon-image category-fallback-image" />
                    </div>
                    <span class="category-name">{{ category.Name }}</span>
                </div>
            </div>
        </div>

        <!-- Category Modal -->
        <div v-if="showCategoryModal" class="category-modal-overlay" @click="closeCategoryModal">
            <div class="category-modal-content" @click.stop>
                <div class="category-modal-header">
                    <h3>{{ $t('sales.kalaStore.categories.allCategories') }}</h3>
                    <button class="close-modal-btn" @click="closeCategoryModal">×</button>
                </div>
                <div class="category-modal-grid">
                    <div 
                        v-for="(category, categoryIndex) in categories" 
                        :key="category.SubCode"
                        class="category-modal-item"
                        @click="goToCategoryPage(category)"
                    >
                        <div class="category-modal-icon-wrapper">
                            <img
                                v-if="category.Image1"
                                :src="getImageSrc(category.Image1)"
                                :alt="category.Name"
                                class="category-modal-image"
                                loading="lazy"
                            />
                            <img v-else :src="getCategoryFallbackIcon(categoryIndex)" :alt="category.Name" class="category-modal-image category-fallback-image" />
                        </div>
                        <span class="category-modal-name">{{ category.Name }}</span>
                        <span class="category-modal-count">{{ getProductCountInCategory(category) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Top Selling Products -->
        <div v-if="topSelling.length > 0" class="products-section">
            <div class="products-grid">
                <div 
                    v-for="product in topSelling" 
                    :key="product.SubCode"
                    class="product-card"
                >
                    <div class="product-image product-gallery-frame">
                        <img :src="getImageSrc(getProductImages(product)[getProductImageIndex(product)] || getProductImage(product))" :alt="$t('sales.kalaStore.products.productImage')">
                        <template v-if="getProductImages(product).length > 1">
                            <button class="gallery-arrow gallery-prev" type="button" aria-label="تصویر قبلی" @click.stop="stepProductImage(product, -1)">‹</button>
                            <button class="gallery-arrow gallery-next" type="button" aria-label="تصویر بعدی" @click.stop="stepProductImage(product, 1)">›</button>
                            <span class="gallery-count">{{ getProductImageIndex(product) + 1 }} / {{ getProductImages(product).length }}</span>
                        </template>
                    </div>
                    <div class="product-info">
                        <div class="product-name">{{ product.Name }}</div>
                        <div class="product-price">
                            {{ formatCurrency(parseFloat(product.Fe1 || 0)) }} {{ $t('sales.kalaStore.common.rial') }}
                        </div>
                        <div class="product-stock" :class="{ 'low-stock': product.StockQuantity < 10 }">
                            {{ $t('sales.kalaStore.products.stock') }}: {{ product.StockQuantity || 0 }} {{ getProductUnit(product) }}
                        </div>
                        <button 
                        style="margin-top: 5px;"
                            class="add-to-cart-btn"
                            @click="addToCart(product)"
                            :disabled="!product.StockQuantity || product.StockQuantity <= 0"
                        >
                            {{ $t('sales.kalaStore.products.addToCart') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- All Products - New Layout -->
        <div class="products-section">
            <div class="section-title1">
                <h3>{{ $t('sales.kalaStore.products.allProducts') }}</h3>
                <div class="products-count">
                    {{ totalProducts }} {{ $t('sales.kalaStore.products.productCount') }}
                </div>
            </div>
            
            <!-- Loading State -->
            <div v-if="loading" class="loading-state">
                <div class="spinner"></div>
                {{ $t('sales.kalaStore.products.loading') }}
            </div>
            
            <!-- Products List - New Horizontal Layout -->
            <div v-else-if="products.length > 0" class="products-list">
                <!-- Group by category -->
                <div v-for="(group, categoryName) in groupedProducts" :key="categoryName" class="product-category-group">
                    <!-- <div class="category-divider">
                        <span class="category-label">{{ categoryName }}</span>
                        <span class="category-count">{{ group.length }} {{ $t('sales.kalaStore.products.productCount') }}</span>
                    </div> -->
                    
                    <div 
                        v-for="product in group" 
                        :key="product.SubCode"
                        class="product-list-item"
                    >
                        <div class="product-content">
                            <div class="product-details-left">
                                <div class="product-title" style="text-align: right;" @click="viewProductDetails(product)">{{ product.Name }}</div>
                                <div class="product-description" style="text-align: right;">
                                    <span v-text="product.Comment_fa || ''"></span>
                                </div>
                                <div class="product-meta-row">
                                    <span class="product-code">
                                        {{ $t('sales.kalaStore.products.code') }}: {{ product.SubCode || product.Commodity_Code }}
                                    </span>
                                    <span class="product-unit">
                                        {{ $t('sales.kalaStore.products.unit') }}: {{ getProductUnit(product) }}
                                    </span>
                                    <span v-if="product.StockQuantity > 0" class="product-stock-info" :class="{ 'low-stock': product.StockQuantity < 10 }">
                                         موجودی  : {{ product.StockQuantity || 0 }} {{ product.Vahed1 || '' }}
                                    </span>
                                </div>
                            </div>
                            
                            <div class="product-actions-right">
                                <!-- Cart Controls -->
                                <div class="cart-controls" :class="{ 'cart-item-controls-open': isInCart(product) }">
                                    <template v-if="isInCart(product)">
                                        <button 
                                            class="qty-control-btn decrement"
                                            @click="decrementQuantity(product)"
                                            :disabled="getCartItemQuantity(product) <= 1"
                                        >
                                            −
                                        </button>
                                        
                                        <input 
                                            type="number" 
                                            :value="getCartItemQuantity(product)"
                                            min="1"
                                            :max="product.StockQuantity || 999"
                                            @input="updateQuantityInput(product, $event)"
                                            class="qty-input-cart"
                                        />
                                        
                                        <button 
                                            class="qty-control-btn increment"
                                            @click="incrementQuantity(product)"
                                        >
                                            +
                                        </button>
                                        
                                        <button 
                                            class="remove-from-cart-btn"
                                            @click="removeFromCart(product)"
                                            title="Remove from cart"
                                        >
                                            ×
                                        </button>
                                    </template>
                                    
                                    <template v-else>
                                        <div >
                                            <button 
                                            class="add-to-cart-btn"
                                            @click="addToCart(product)"
                                        >
                                            + {{ $t('sales.kalaStore.products.addToCart') }}
                                        </button>
                                        </div>
                                        
                                    </template>
                                    <div v-if="product.Fe1 > 0" class="product-price">
                                         هر {{ product.Vahed1 }} {{ formatCurrency(parseFloat(product.Fe1 || 0)) }} {{ $t('sales.kalaStore.common.rial') }} 
                                    </div>
                                </div>

                                <!-- Note Input for items in cart -->
                                <div v-if="isInCart(product)" class="cart-note-input">
                                    <input 
                                        type="text"
                                        :value="getCartItemNote(product)"
                                        @input="updateNoteInput(product, $event)"
                                        :placeholder="'توضیحات برای ' + product.Name"
                                        class="note-input-field"
                                    />
                                    <span class="note-icon">📝</span>
                                </div>
                                
                            </div>
                                
                            <div v-if="product.Image1 != 'No Image'" class="product-image-right product-gallery-frame" @click="viewProductDetails(product)">
                                <img :src="getImageSrc(getProductImages(product)[getProductImageIndex(product)] || getProductImage(product))" :alt="$t('sales.kalaStore.products.productImage')">
                                <template v-if="getProductImages(product).length > 1">
                                    <button class="gallery-arrow gallery-prev" type="button" aria-label="تصویر قبلی" @click.stop="stepProductImage(product, -1)">‹</button>
                                    <button class="gallery-arrow gallery-next" type="button" aria-label="تصویر بعدی" @click.stop="stepProductImage(product, 1)">›</button>
                                    <span class="gallery-count">{{ getProductImageIndex(product) + 1 }} / {{ getProductImages(product).length }}</span>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Empty State -->
            <div v-else class="empty-products">
                {{ $t('sales.kalaStore.products.empty') }}
            </div>
            
            <!-- Pagination -->
            <div v-if="totalPages > 1" class="pagination">
                <button 
                    @click="prevPage" 
                    :disabled="currentPage === 1"
                    class="pagination-btn"
                >
                    {{ $t('sales.kalaStore.pagination.previous') }}
                </button>
                
                <div class="page-numbers">
                    <span 
                        v-for="page in visiblePages" 
                        :key="page"
                        @click="goToPage(page)"
                        :class="{ 
                            'page-number': true, 
                            'active': page === currentPage,
                            'disabled': page === '...'
                        }"
                    >
                        {{ page }}
                    </span>
                </div>
                
                <button 
                    @click="nextPage" 
                    :disabled="currentPage === totalPages"
                    class="pagination-btn"
                >
                    {{ $t('sales.kalaStore.pagination.next') }}
                </button>
            </div>
        </div>

        <!-- Product Details Modal -->
        <div v-if="showProductModal" class="product-modal-overlay" @click="closeProductModal">
            <div class="product-modal-content" @click.stop>
                <div class="product-modal-header">
                    
                    <button class="close-modal-btn" @click="closeProductModal">×</button>
                </div>
                
                <div class="product-modal-body" v-if="selectedProduct">
                    <div v-if="selectedProduct.Image1 != 'No Image'" class="product-modal-image product-gallery-frame">
                        <img :src="getImageSrc(getModalImage() || getProductImage(selectedProduct))" :alt="$t('sales.kalaStore.products.productImage')">
                        <template v-if="getProductImages(selectedProduct).length > 1">
                            <button class="gallery-arrow gallery-prev" type="button" aria-label="تصویر قبلی" @click.stop="stepModalImage(-1)">‹</button>
                            <button class="gallery-arrow gallery-next" type="button" aria-label="تصویر بعدی" @click.stop="stepModalImage(1)">›</button>
                            <span class="gallery-count">{{ modalImageIndex + 1 }} / {{ getProductImages(selectedProduct).length }}</span>
                        </template>
                    </div>
                    
                    <div class="product-modal-info">
                        <h3>{{ selectedProduct.Name }}</h3>
                        
                        <div class="product-modal-price">
                            <span class="product-modal-price-label">مبلغ کالا</span>
                            هر {{ selectedProduct.Vahed1 }} {{ formatCurrency(parseFloat(selectedProduct.Fe1 || 0)) }} {{ $t('sales.kalaStore.common.rial') }} 
                        </div>
                        
                        <div class="product-modal-price-words">
                            {{ numberToPersianCurrency(parseFloat(selectedProduct.Fe1 || 0)) }}
                        </div>

                        <div class="product-modal-stock" :class="{ 
                            'in-stock': selectedProduct.StockQuantity > 0,
                            'out-of-stock': !selectedProduct.StockQuantity || selectedProduct.StockQuantity <= 0
                        }">
                            <span class="stock-icon" v-if="selectedProduct.StockQuantity > 0">✓</span>
                            <span class="stock-icon" v-else>✕</span>
                            {{ $t('sales.kalaStore.productDetails.status') }}: 
                            <span v-if="selectedProduct.StockQuantity > 0">
                                {{ $t('sales.kalaStore.productDetails.inStock') }} ({{ selectedProduct.StockQuantity }} {{ $t('sales.kalaStore.common.items') }})
                            </span>
                            <span v-else>{{ $t('sales.kalaStore.productDetails.outOfStock') }}</span>
                        </div>
                        
                        <p v-if="selectedProduct.Commodity_comment" style="margin-left: auto; text-align: right;">توضیحات</p>
                        <div v-if="isAccountant" class="product-modal-details">
                            <div class="detail-row">
                                <span class="detail-label">{{ $t('sales.kalaStore.productDetails.productCode') }}</span>
                                <span class="detail-value">{{ selectedProduct.SubCode || '-' }}</span>
                                <span class="detail-label">{{ $t('sales.kalaStore.productDetails.manualCode') }}</span>
                                <span class="detail-value">{{ selectedProduct.Commodity_Code || '-' }}</span>
                                <span class="detail-label">{{ $t('sales.kalaStore.productDetails.barcode') }}</span>
                                <span class="detail-value">{{ selectedProduct.BarCode || '-' }}</span>
                            </div>
                            <div class="detail-row">
                                <span class="detail-label">{{ $t('sales.kalaStore.productDetails.unit') }}</span>
                                <span class="detail-value">{{ selectedProduct.Vahed1 || '-' }}</span>
                            </div>
                        </div>
                        <div v-if="selectedProduct.Commodity_comment" class="product-modal-details">
                            <div class="detail-row">
                                <p>{{ selectedProduct.Commodity_comment }}</p>
                            </div>
                        </div>
                        
                        <!-- <div class="product-modal-description" >
                            <p>{{ selectedProduct.Commodity_comment }}</p>
                        </div> -->
                        
                        <!-- Add to Cart Section -->
                        <div v-if="selectedProduct.StockQuantity > 0" class="add-to-cart-section">
                            <div class="quantity-controls">
                                <button class="qty-btn minus" @click="modalQuantity > 1 ? modalQuantity-- : null">
                                    −
                                </button>
                                <input 
                                    type="number" 
                                    v-model.number="modalQuantity"
                                    min="1"
                                    :max="selectedProduct.StockQuantity"
                                    class="qty-input"
                                />
                                <button class="qty-btn plus" @click="modalQuantity < selectedProduct.StockQuantity ? modalQuantity++ : null">
                                    +
                                </button>
                            </div>
                            
                            <button 
                                class="add-to-cart-modal-btn"
                                @click="addToCartWithQuantity(selectedProduct, modalQuantity)"
                            >
                                <span v-if="isInCart(selectedProduct)">
                                    ✓ {{ $t('sales.kalaStore.productDetails.updateCart') }}
                                </span>
                                <span v-else>
                                    + {{ $t('sales.kalaStore.productDetails.addToCart') }}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Checkout/Invoice Modal -->
        <div v-if="showCheckoutModal" class="checkout-modal-overlay" @click="closeCheckoutModal">
            <div class="checkout-modal-content" @click.stop>
                <div class="checkout-modal-header">
                    <h3>🛒 {{ $t('sales.kalaStore.checkout.title') }}</h3>
                    <button class="close-checkout-btn" @click="closeCheckoutModal">×</button>
                </div>
                
                <div class="checkout-modal-body">
                    <!-- Invoice Summary -->
                    <div class="invoice-summary">
                        <h4>{{ $t('sales.kalaStore.checkout.invoiceSummary') }}</h4>
                        <div class="summary-card">
                            <div class="summary-row">
                                <span>{{ $t('sales.kalaStore.checkout.date') }}:</span>
                                <span>{{ invoiceData.Header.Dates || $t('sales.kalaStore.checkout.notSet') }}</span>
                            </div>
                            <div class="summary-row">
                                <span>{{ $t('sales.kalaStore.checkout.dueDate') }}:</span>
                                <span>{{ invoiceData.Header.Saresid || $t('sales.kalaStore.checkout.notSet') }}</span>
                            </div>
                            <div class="summary-row">
                                <span>{{ $t('sales.kalaStore.checkout.itemsCount') }}:</span>
                                <span>{{ cartItems.length }} {{ $t('sales.kalaStore.common.items') }}</span>
                            </div>
                            <div class="summary-row">
                                <span>{{ $t('sales.kalaStore.checkout.totalAmount') }}:</span>
                                <span class="amount">{{ formatCurrency(calculateSubTotal()) }} {{ $t('sales.kalaStore.common.rial') }}</span>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Cart Items Review -->
                    <div class="cart-review">
                        <h4>{{ $t('sales.kalaStore.checkout.cartItems') }}</h4>
                        <div class="cart-review-items">
                            <div v-for="(item, index) in cartItems" :key="index" class="cart-review-item">
                                <div class="item-info">
                                    <span class="item-name">{{ item.name }}</span>
                                    <span class="item-quantity">{{ item.quantity }} × {{ formatCurrency(item.price) }}</span>
                                </div>
                                <div class="item-total">
                                    {{ formatCurrency(item.price * item.quantity) }} {{ $t('sales.kalaStore.common.rial') }}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Deductions (کسورات) -->
                    <div class="deductions-section">
                        <div class="section-header">
                            <h4>{{ $t('sales.kalaStore.checkout.deductions') }}</h4>
                            <button v-if="accountValidDec.length > 0" class="add-item-btn" @click="addDecItem">
                                {{ $t('sales.kalaStore.checkout.addDeduction') }}
                            </button>
                        </div>
                        
                        <div v-if="invoiceData.Dec.length === 0" class="empty-section">
                            <p>{{ $t('sales.kalaStore.checkout.noDeductions') }}</p>
                        </div>
                        
                        <div v-else>
                            <div v-for="(item, index) in invoiceData.Dec" :key="index" class="deduction-item">
                                <div class="item-row">
                                    <select 
                                        v-model="item.code_hesab" 
                                        @change="updateDecItem(index, 'code_hesab', $event.target.value)"
                                        class="account-select"
                                    >
                                        <option v-for="acc in accountValidDec" :key="acc.code_hesab" :value="acc.code_hesab">
                                            {{ acc.name }} ({{ acc.code_hesab }})
                                        </option>
                                    </select>
                                    
                                    <input 
                                        type="number" 
                                        v-model.number="item.Darsad" 
                                        @input="updateDecItem(index, 'Darsad', $event.target.value)"
                                        :placeholder="$t('sales.kalaStore.checkout.percent')"
                                        class="percent-input"
                                    />
                                    
                                    <input 
                                        type="number" 
                                        v-model.number="item.mablDec" 
                                        @input="updateDecItem(index, 'mablDec', $event.target.value)"
                                        :placeholder="$t('sales.kalaStore.checkout.amount')"
                                        class="amount-input"
                                    />
                                    
                                    <button class="remove-item-btn" @click="removeDecItem(index)">✕</button>
                                </div>
                            </div>
                        </div>
                        
                        <div v-if="invoiceData.Dec.length > 0" class="total-row">
                            <span>{{ $t('sales.kalaStore.checkout.totalDeductions') }}:</span>
                            <span class="amount negative">{{ formatCurrency(calculateTotalDec()) }} {{ $t('sales.kalaStore.common.rial') }}</span>
                        </div>
                    </div>
                    
                    <!-- Additions (اضافات) -->
                    <div class="additions-section">
                        <div class="section-header">
                            <h4>{{ $t('sales.kalaStore.checkout.additions') }}</h4>
                            <button v-if="accountValidInc.length > 0" class="add-item-btn" @click="addIncItem">
                                {{ $t('sales.kalaStore.checkout.addAddition') }}
                            </button>
                        </div>
                        
                        <div v-if="invoiceData.Inc.length === 0" class="empty-section">
                            <p>{{ $t('sales.kalaStore.checkout.noAdditions') }}</p>
                        </div>
                        
                        <div v-else>
                            <div v-for="(item, index) in invoiceData.Inc" :key="index" class="addition-item">
                                <div class="item-row">
                                    <select 
                                        v-model="item.code_hesab" 
                                        @change="updateIncItem(index, 'code_hesab', $event.target.value)"
                                        class="account-select"
                                    >
                                        <option v-for="acc in accountValidInc" :key="acc.code_hesab" :value="acc.code_hesab">
                                            {{ acc.name }} ({{ acc.code_hesab }})
                                        </option>
                                    </select>
                                    
                                    <input 
                                        type="number" 
                                        v-model.number="item.Darsad" 
                                        @input="updateIncItem(index, 'Darsad', $event.target.value)"
                                        :placeholder="$t('sales.kalaStore.checkout.percent')"
                                        class="percent-input"
                                    />
                                    
                                    <input 
                                        type="number" 
                                        v-model.number="item.mablInc" 
                                        @input="updateIncItem(index, 'mablInc', $event.target.value)"
                                        :placeholder="$t('sales.kalaStore.checkout.amount')"
                                        class="amount-input"
                                    />
                                    
                                    <button class="remove-item-btn" @click="removeIncItem(index)">✕</button>
                                </div>
                            </div>
                        </div>
                        
                        <div v-if="invoiceData.Inc.length > 0" class="total-row">
                            <span>{{ $t('sales.kalaStore.checkout.totalAdditions') }}:</span>
                            <span class="amount positive">{{ formatCurrency(calculateTotalInc()) }} {{ $t('sales.kalaStore.common.rial') }}</span>
                        </div>
                    </div>
                    
                    <!-- Final Total -->
                    <div class="final-total">
                        <div class="total-row large">
                            <span>{{ $t('sales.kalaStore.checkout.finalAmount') }}:</span>
                            <span class="amount final">{{ formatCurrency(calculateFinalTotal()) }} {{ $t('sales.kalaStore.common.rial') }}</span>
                        </div>
                    </div>
                    
                    <!-- Comments -->
                    <div class="comments-section">
                        <label for="invoice-comments">{{ $t('sales.kalaStore.checkout.comments') }}:</label>
                        <textarea 
                            id="invoice-comments"
                            v-model="invoiceData.Header.Comments"
                            :placeholder="$t('sales.kalaStore.checkout.commentsPlaceholder')"
                            rows="3"
                            class="comments-input"
                        ></textarea>
                    </div>
                </div>
                
                <div class="checkout-modal-footer">
                    <button class="cancel-btn" @click="closeCheckoutModal" :disabled="checkoutLoading">
                        {{ $t('sales.kalaStore.checkout.cancel') }}
                    </button>
                    <button class="submit-invoice-btn" @click="submitInvoice" :disabled="checkoutLoading">
                        <span v-if="checkoutLoading" class="spinner-small"></span>
                        <span v-else>✓ {{ $t('sales.kalaStore.checkout.submit') }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, ref, onMounted, reactive } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { SalesService } from '@/services/api'
import Navbar from '@/components/Navbar.vue'
import { useRouter } from 'vue-router'
import ShoppingCart from '@/components/ShoppingCartFloating.vue'
import sofaCategoryIcon from '@/assets/Icons/store/sofa.png'
import bedCategoryIcon from '@/assets/Icons/store/bed.png'

export default {
    name: 'KalaStore',
    props: {
        isAccountant: { type: Boolean, default: false }
    },
    components: {
        Navbar,
        ShoppingCart
    },
    setup(props) {
        const authStore = useAuthStore()
        const router = useRouter()
        const shoppingCart = ref(null)
        const storeLayout = ref('minimal')
        const companyName = computed(() => authStore.selectedCompany?.company_name || authStore.selectedCompany?.CompanyName || '')
        
        // ============= OPTIMIZATION: Debounce timer & Cache =============
        let searchDebounceTimer = null
        const productCache = new Map()
        const CACHE_TTL = 60000 // 1 minute

        function getCacheKey(params) {
            return JSON.stringify(params)
        }

        function getCachedData(key) {
            const cached = productCache.get(key)
            if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
                return cached.data
            }
            return null
        }

        function setCachedData(key, data) {
            productCache.set(key, {
                data: data,
                timestamp: Date.now()
            })
        }

        function clearCache() {
            productCache.clear()
        }
        
        // ============= STATE =============
        const products = ref([])
        const categories = ref([])
        const categoryCounts = ref({})
        const topSelling = ref([])
        const loading = ref(false)
        const searchQuery = ref('')
        const showFilterPanel = ref(false)
        const showProductModal = ref(false)
        const selectedProduct = ref(null)
        const modalQuantity = ref(1)
        const productImageIndexes = ref({})
        const modalImageIndex = ref(0)
        const showCategoryModal = ref(false)
        const showCheckoutModal = ref(false)
        const checkoutLoading = ref(false)
        const settingNet = ref(null)
        const accountValidDec = ref([])
        const accountValidInc = ref([])
        const cartItems = ref([])
        const isInitialLoad = ref(true)
        
        // Track if we're in search mode
        const isSearchMode = ref(false)
        
        // ============= INVOICE DATA =============
        const invoiceData = reactive({
            Header: {
                Ac: 2,
                Act: 1,
                Dates: null,
                Ei5: null,
                Operator: null,
                Arz: 1,
                Erja: 0,
                Duration: 0,
                Saresid: null,
                Comments: "",
                Pelak: "",
                In_Oto: "",
                In_Bar: "",
                In_Driver: 0,
                In_Fe: 0,
                To_Time: 0,
                Rec_num: 0,
                Rec_Date: ""
            },
            Body: [],
            Dec: [],
            Inc: [],
            Payment: []
        })
        
        // ============= PAGINATION =============
        const currentPage = ref(1)
        const pageSize = ref(10) // SelectCommodity3 uses a fixed page size of 10.
        const totalProducts = ref(0)
        const totalPages = ref(1)
        
        // ============= FILTERS =============
        const filters = ref({
            minPrice: null,
            maxPrice: null,
            category: null,
            inStockOnly: false
        })
        
        const activeCategory = ref(null)
        
        // ============= COMPUTED =============
        const cartTotalItems = computed(() => {
            return cartItems.value.reduce((total, item) => total + item.quantity, 0)
        })
        
        const cartTotalAmount = computed(() => {
            return cartItems.value.reduce((total, item) => total + (item.price * item.quantity), 0)
        })
        
        const visiblePages = computed(() => {
            const pages = []
            const maxVisible = 5
            
            if (totalPages.value <= maxVisible) {
                for (let i = 1; i <= totalPages.value; i++) {
                    pages.push(i)
                }
            } else {
                pages.push(1)
                if (currentPage.value > 3) pages.push('...')
                
                const start = Math.max(2, currentPage.value - 1)
                const end = Math.min(totalPages.value - 1, currentPage.value + 1)
                for (let i = start; i <= end; i++) pages.push(i)
                
                if (currentPage.value < totalPages.value - 2) pages.push('...')
                pages.push(totalPages.value)
            }
            return pages
        })

        const groupedProducts = computed(() => {
            const grouped = {}
            products.value.forEach(product => {
                const categoryName = product.CategoryName || 'Uncategorized'
                if (!grouped[categoryName]) {
                    grouped[categoryName] = []
                }
                grouped[categoryName].push(product)
            })
            return grouped
        })

        // Add these to your state
const showInvoiceConfirmationModal = ref(false)
const invoiceConfirmationLoading = ref(false)
const invoiceConfirmationData = ref(null)
const invoiceConfirmationError = ref(null)
const termsAccepted = ref(false)
const savedInvoiceResponse = ref(null)

// ============= INVOICE CONFIRMATION METHODS =============

const showInvoiceConfirmation = async (invoiceId, ac, act) => {
    showInvoiceConfirmationModal.value = true
    invoiceConfirmationLoading.value = true
    invoiceConfirmationError.value = null
    termsAccepted.value = false
    console.log('I am here')
    
    try {
        // Fetch invoice details using the existing endpoint
        const response = await SalesService.getInvoiceDetail(invoiceId, ac, act)
        
        if (response.data.success) {
            invoiceConfirmationData.value = {
                ...response.data,
                details: response.data.details || [],
                invoice_info: response.data.invoice_info || {}
            }
        } else {
            invoiceConfirmationError.value = response.data.message || 'خطا در دریافت جزئیات فاکتور'
        }
    } catch (error) {
        console.error('Error fetching invoice details:', error)
        invoiceConfirmationError.value = 'خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید.'
    } finally {
        invoiceConfirmationLoading.value = false
    }
}

const closeInvoiceConfirmationModal = () => {
    showInvoiceConfirmationModal.value = false
    invoiceConfirmationData.value = null
    invoiceConfirmationError.value = null
    termsAccepted.value = false
    savedInvoiceResponse.value = null
}

const onTermsChange = () => {
    // Terms changed - button will be enabled/disabled based on checkbox
}

const confirmInvoice = async () => {
    if (!termsAccepted.value) return
    
    const invoiceId = invoiceConfirmationData.value?.invoice_info?.invoice_id
    if (!invoiceId) {
        alert('شناسه فاکتور یافت نشد')
        return
    }
    
    invoiceConfirmationLoading.value = true
    
    try {
        const response = await SalesService.okInvoice(invoiceId)
        
        if (response.data.success) {
            alert('فاکتور با موفقیت تایید شد!')
            closeInvoiceConfirmationModal()
            // Clear cart
            if (shoppingCart.value) {
                shoppingCart.value.clearCart()
            }
        } else {
            alert(`خطا در تایید فاکتور: ${response.data.message}`)
        }
    } catch (error) {
        console.error('Error confirming invoice:', error)
        alert('خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید.')
    } finally {
        invoiceConfirmationLoading.value = false
    }
}

// ============= UPDATE SUBMIT INVOICE =============
const submitInvoice = async () => {
    if (invoiceData.Body.length === 0) {
        alert('هیچ کالایی برای ثبت وجود ندارد')
        return
    }
    
    checkoutLoading.value = true
    
    try {
        const response = await SalesService.saveInvoice(invoiceData)
        
        if (response.data.success) {
            // Close checkout modal
            showCheckoutModal.value = false
            
            // Store invoice ID from response (you may need to return it from backend)
            const invoiceId = response.data.invoice_number || null
            const ac = invoiceData.Header.Ac || 2
            const act = invoiceData.Header.Act || 1
            console.log('hahaha ' + invoiceId + ' ' + ac + ' ' + act)
            
            // Show confirmation modal with invoice details
            if (invoiceId) {
                await showInvoiceConfirmation(invoiceId, ac, act)
            } else {
                // Fallback: show manual confirmation
                showManualConfirmation()
            }
        } else {
            alert(`خطا در ثبت فاکتور: ${response.data.message}`)
        }
    } catch (error) {
        console.error('Error submitting invoice:', error)
        alert('خطا در ارتباط با سرور. لطفا دوباره تلاش کنید.')
    } finally {
        checkoutLoading.value = false
    }
}

// Fallback method
const showManualConfirmation = () => {
    showInvoiceConfirmationModal.value = true
    invoiceConfirmationLoading.value = false
    termsAccepted.value = false
    
    invoiceConfirmationData.value = {
        details: invoiceData.Body.map(item => ({
            KalaName: `کد کالا: ${item.Commodity_code}`,
            value1: item.value1,
            Fe: item.Fe,
            money: item.value1 * item.Fe
        })),
        Num: 'جدید',
        Dates: invoiceData.Header.Dates,
        invoice_info: {
            invoice_id: Date.now(),
            total_amount: calculateSubTotal()
        }
    }
}

// ============= ADD TO RETURN =============
        
        // ============= MAP FUNCTION =============
        const mapCommodityToProduct = (item) => {
            return {
                MainCode: item.MainCode || 0,
                SubCode: String(item.SubCode || ''),
                Commodity_Code: String(item.Commodity_Code || ''),
                BarCode: String(item.BarCode || ''),
                Name: item.HandTitle || '',
                Fe1: String(item.Fe1 || ''),
                Vahed1: item.Vahed1 || '',
                Vahed2: item.Vahed2 || '',
                Kala4: String(item.Kala4 || ''),
                Commodity_comment: item.Commodity_comment || '',
                Comment_fa: item.Comment_fa || '',
                Comment_en: item.Comment_en || '',
                catalog_Ad: item.catalog_Ad || '',
                Image1: item.Image1 || null,
                Image2: item.Image2 || null,
                Images: Array.isArray(item.Images) ? item.Images.filter(Boolean) : [],
                StockQuantity: item.Mojoodi || 0,
                Discont: item.Discont || 0,
                CategoryName: 'Products'
            }
        }

        const getImageSrc = (image) => {
            if (!image || image === 'No Image') return ''
            const value = String(image).trim()
            if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('//') || value.startsWith('data:') || value.startsWith('/')) return value
            return `data:image/jpeg;base64,${value}`
        }

        const getProductImages = (product) => {
            if (!product) return []
            const candidates = [
                ...(Array.isArray(product.Images) ? product.Images : []),
                product.Image1,
                product.Image2,
            ]
            return [...new Set(candidates
                .filter(value => value && value !== 'No Image')
                .map(value => String(value).trim())
                .filter(Boolean))]
        }

        const getProductImageIndex = (product) => productImageIndexes.value[String(product?.SubCode || '')] || 0

        const stepProductImage = (product, direction) => {
            const count = getProductImages(product).length
            if (count < 2) return
            const key = String(product.SubCode || '')
            productImageIndexes.value[key] = (getProductImageIndex(product) + direction + count) % count
        }

        const getModalImage = () => getProductImages(selectedProduct.value)[modalImageIndex.value] || ''

        const stepModalImage = (direction) => {
            const count = getProductImages(selectedProduct.value).length
            if (count > 1) modalImageIndex.value = (modalImageIndex.value + direction + count) % count
        }

        const getCategoryFallbackIcon = (index) => index % 2 === 0 ? sofaCategoryIcon : bedCategoryIcon

        const fetchAllCommodityPages = async (typ) => {
            const firstPage = await SalesService.selectCommodity3(typ, 1)
            if (!firstPage.data?.success) return []
            const allItems = [...(firstPage.data.data || [])]
            for (let page = 2; page <= (firstPage.data.total_pages || 1); page += 1) {
                const response = await SalesService.selectCommodity3(typ, page)
                if (response.data?.success) allItems.push(...(response.data.data || []))
            }
            return allItems
        }

        // ============= CHECK IF SEARCH/FILTERS ARE ACTIVE =============
        const hasActiveSearchOrFilters = () => {
            return searchQuery.value && searchQuery.value.trim() !== '' ||
                   filters.value.category ||
                   filters.value.minPrice !== null ||
                   filters.value.maxPrice !== null ||
                   filters.value.inStockOnly
        }

        // ============= METHODS =============
        
        const addToCart = (product) => {
            if (shoppingCart.value) {
                shoppingCart.value.addToCart(product)
            }
        }
        
        const addToCartWithQuantity = (product, quantity) => {
            if (shoppingCart.value) {
                shoppingCart.value.addToCart(product, quantity)
                closeProductModal()
            }
        }
        
        const isInCart = (product) => {
            return shoppingCart.value ? shoppingCart.value.isInCart(product) : false
        }

        const getCartItemQuantity = (product) => {
            if (shoppingCart.value) {
                const cartItems = shoppingCart.value.cartItems
                const itemKey = product.SubCode
                const cartItem = cartItems.find(item => item.kala_id === itemKey)
                return cartItem ? cartItem.quantity : 0
            }
            return 0
        }

        const incrementQuantity = (product) => {
            if (shoppingCart.value) {
                shoppingCart.value.addToCart(product, 1)
            }
        }

        const decrementQuantity = (product) => {
            if (shoppingCart.value) {
                const currentQty = getCartItemQuantity(product)
                if (currentQty > 0) {
                    const newQty = currentQty - 1
                    shoppingCart.value.updateCartItemQuantity(product, newQty)
                }
            }
        }

        const removeFromCart = (product) => {
            if (shoppingCart.value) {
                shoppingCart.value.updateCartItemQuantity(product, 0)
            }
        }

        // ============= FETCH CATEGORIES =============
        const fetchCategories = async () => {
            try {
                const items = await fetchAllCommodityPages(0)
                categories.value = items.map(mapCommodityToProduct)
            } catch (error) {
                console.error('Error fetching categories:', error)
            }
        }

        // ============= FETCH PRODUCTS =============
        const fetchProducts = async (isFiltered = false) => {
            if (!authStore.isAuthenticated) return
            
            const hasSearchOrFilters = hasActiveSearchOrFilters()
            isSearchMode.value = hasSearchOrFilters
            
            // Generate cache key
            const cacheKey = getCacheKey({
                page: currentPage.value,
                pageSize: pageSize.value,
                search: searchQuery.value,
                filters: filters.value,
                isFiltered,
                mode: hasSearchOrFilters ? 'search' : 'normal'
            })
            
            // Check cache for normal mode only
            if (!isInitialLoad.value && !hasSearchOrFilters) {
                const cachedData = getCachedData(cacheKey)
                if (cachedData) {
                    products.value = cachedData.products
                    totalProducts.value = cachedData.totalProducts
                    totalPages.value = cachedData.totalPages
                    return
                }
            }
            
            loading.value = true
            
            try {
                let response;
                
                // Use search endpoint if search query or filters exist
                if (hasSearchOrFilters) {
                    const searchRequest = {
                        search_query: searchQuery.value || null,
                        min_price: filters.value.minPrice,
                        max_price: filters.value.maxPrice,
                        category_id: filters.value.category,
                        in_stock_only: filters.value.inStockOnly,
                        page: currentPage.value,
                        page_size: pageSize.value
                    };
                    
                    response = await SalesService.searchKala(searchRequest);
                    
                    if (response.data.success) {
                        products.value = response.data.children_kala || [];
                        totalProducts.value = response.data.total_count || 0;
                        totalPages.value = response.data.total_pages || 1;
                    }
                } else {
                    response = await SalesService.selectCommodity3(1, currentPage.value);
                    
                    if (response.data.success) {
                        products.value = response.data.data.map(mapCommodityToProduct);
                        totalProducts.value = response.data.total_count || 0;
                        totalPages.value = response.data.total_pages || 1;
                        
                        // Cache the result
                        setCachedData(cacheKey, {
                            products: products.value,
                            totalProducts: totalProducts.value,
                            totalPages: totalPages.value,
                            categories: categories.value
                        })
                    }
                }
                
            } catch (error) {
                console.error('Error fetching products:', error);
            } finally {
                loading.value = false;
                isInitialLoad.value = false;
            }
        }
        
        // ============= DEBOUNCED SEARCH =============
        const debouncedSearch = () => {
            if (searchDebounceTimer) {
                clearTimeout(searchDebounceTimer)
            }
            searchDebounceTimer = setTimeout(() => {
                currentPage.value = 1
                clearCache()
                fetchProducts()
            }, 300)
        }
        
        // ============= FETCH CATEGORY COUNTS =============
        const fetchCategoryProductCounts = async () => {
            try {
                // Get all children products to count by category
                const items = await fetchAllCommodityPages(1)
                const counts = {}
                items.forEach(item => {
                    const mainCode = String(item.MainCode || '')
                    if (mainCode) counts[mainCode] = (counts[mainCode] || 0) + 1
                })
                categoryCounts.value = counts
            } catch (error) {
                console.error('Error fetching category counts:', error)
            }
        }
        
        // ============= FETCH TOP SELLING =============
        const fetchTopSelling = async () => {
            try {
                // typ=2 for top selling
                const response = await SalesService.selectCommodity3(2, 1)
                if (response.data.success) {
                    topSelling.value = response.data.data.slice(0, 8).map(mapCommodityToProduct)
                }
            } catch (error) {
                console.error('Error fetching top selling:', error)
            }
        }
        
        const handleSearch = () => {
            currentPage.value = 1
            clearCache()
            fetchProducts()
        }
        
        const viewProductDetails = (product) => {
            selectedProduct.value = product
            modalImageIndex.value = 0
            modalQuantity.value = 1
            showProductModal.value = true
        }
        
        const closeProductModal = () => {
            showProductModal.value = false
            selectedProduct.value = null
        }
        
        const toggleFilterPanel = () => {
            showFilterPanel.value = !showFilterPanel.value
        }
        
        const closeFilterPanel = () => {
            showFilterPanel.value = false
        }
        
        const applyFilters = async () => {
            currentPage.value = 1
            clearCache()
            await fetchProducts(true)
            closeFilterPanel()
        }
        
        const resetFilters = () => {
            filters.value = {
                minPrice: null,
                maxPrice: null,
                category: null,
                inStockOnly: false
            }
            searchQuery.value = ''
            activeCategory.value = null
            currentPage.value = 1
            isSearchMode.value = false
            clearCache()
            fetchProducts(false)
        }
        
        const loadCategoryProducts = async (categoryId) => {
            activeCategory.value = categoryId
            filters.value.category = categoryId
            currentPage.value = 1
            clearCache()
            await fetchProducts(true)
        }
        
        const getProductImage = () => {
            return require('@/assets/Icons/product.png')
        }
        
        const formatCurrency = (amount) => {
            return new Intl.NumberFormat('fa-IR').format(amount)
        }

        const numberToPersianCurrency = (amount) => {
            const number = Math.round(Number(amount) || 0)
            if (number === 0) return 'صفر ریال'

            const ones = ['صفر', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه', 'ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نوزده']
            const tens = ['', '', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود']
            const hundreds = ['', 'یکصد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد']
            const scales = ['', 'هزار', 'میلیون', 'میلیارد', 'تریلیون', 'کوادریلیون']

            const underThousand = (value) => {
                const parts = []
                const hundred = Math.floor(value / 100)
                const remainder = value % 100
                if (hundred) parts.push(hundreds[hundred])
                if (remainder < 20 && remainder > 0) {
                    parts.push(ones[remainder])
                } else if (remainder >= 20) {
                    parts.push(tens[Math.floor(remainder / 10)])
                    if (remainder % 10) parts.push(ones[remainder % 10])
                }
                return parts.join(' و ')
            }

            let remaining = Math.abs(number)
            let scaleIndex = 0
            const groups = []
            while (remaining > 0 && scaleIndex < scales.length) {
                const group = remaining % 1000
                if (group) groups.unshift([underThousand(group), scales[scaleIndex]])
                remaining = Math.floor(remaining / 1000)
                scaleIndex += 1
            }
            if (remaining > 0) return `${formatCurrency(number)} ریال`

            const words = groups.map(([groupWords, scale]) => scale ? `${groupWords} ${scale}` : groupWords).join(' و ')
            return `${number < 0 ? 'منفی ' : ''}${words} ریال`
        }
        
        const prevPage = async () => {
            if (currentPage.value > 1) {
                currentPage.value--
                await fetchProducts()
                scrollToCategories()
            }
        }

        const scrollToCategories = () => {
            const element = document.getElementById('categories-section1')
            if (element) {
                element.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                })
            }
        }

        const nextPage = async () => {
            if (currentPage.value < totalPages.value) {
                currentPage.value++
                await fetchProducts()
                scrollToCategories()
            }
        }

        const goToPage = async (page) => {
            if (page !== '...' && page !== currentPage.value) {
                currentPage.value = page
                await fetchProducts()
                scrollToCategories()
            }
        }

        const goBack = () => {
            router.push(props.isAccountant ? '/accountant/customer-search-store' : '/select-company')
        }

        const goToCategoryPage = (category) => {
            router.push({
                name: props.isAccountant ? 'AccountantCategoryPage' : 'CategoryPage',
                params: { id: category.SubCode },
                query: { name: category.Name }
            })
        }

        const getProductCountInCategory = (category) => {
            const mainCode = category.SubCode
            return categoryCounts.value[mainCode] || '-'
        }

        const getProductUnit = (product) => {
            if (product.Kala4 === "1.0") {
                return product.Vahed2 
            }
            return product.Vahed1
        }

        const openCategoryModal = () => {
            showCategoryModal.value = true
        }

        const closeCategoryModal = () => {
            showCategoryModal.value = false
        }

        const selectCategoryFromModal = (category) => {
            loadCategoryProducts(category.MainCode)
            closeCategoryModal()
        }

        // ============= CART METHODS =============
        const updateQuantityInput = (product, event) => {
            if (shoppingCart.value) {
                let newQuantity = parseFloat(event.target.value)
                
                if (isNaN(newQuantity) || newQuantity < 1) {
                    newQuantity = 1
                }
                
                shoppingCart.value.updateCartItemQuantity(product, newQuantity)
            }
        }

        const getCartItemNote = (product) => {
            if (shoppingCart.value) {
                const cartItems = shoppingCart.value.cartItems
                const itemKey = product.SubCode
                const cartItem = cartItems.find(item => item.kala_id === itemKey)
                return cartItem ? cartItem.note || '' : ''
            }
            return ''
        }

        const updateNoteInput = (product, event) => {
            if (shoppingCart.value) {
                const note = event.target.value
                const itemKey = product.SubCode
                const cartItems = shoppingCart.value.cartItems
                const cartItem = cartItems.find(item => item.kala_id === itemKey)
                
                if (cartItem) {
                    cartItem.note = note
                    localStorage.setItem('shoppingCart', JSON.stringify(cartItems))
                    shoppingCart.value.$emit('cart-updated', {
                        items: cartItems,
                        totalItems: shoppingCart.value.cartTotalItems,
                        totalAmount: shoppingCart.value.cartTotalAmount
                    })
                }
            }
        }

        const handleCartUpdate = (data) => {
            cartItems.value = data.items || []
        }

        const handleCheckout = (data) => {
            if (data && data.items && data.items.length > 0) {
                invoiceData.Body = data.items.map(item => ({
                    Codeing: 1,
                    Commodity_code: parseInt(item.kala_id) || 0,
                    value1: parseFloat(item.quantity) || 0,
                    Fe: parseFloat(item.price) || 0,
                    Precent: 0.0,
                    Discount: 0.0,
                    DScot: 0.0,
                    Scot: 0.0,
                    DToll: 0.0,
                    Toll: 0.0,
                    Depot: 6
                }))
                
                const now = new Date()
                const persianDate = new Intl.DateTimeFormat('fa-IR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                }).format(now).replace(/\//g, '/')
                
                invoiceData.Header.Dates = persianDate
                invoiceData.Header.Saresid = persianDate
                
                const user = JSON.parse(localStorage.getItem('user') || '{}')
                invoiceData.Header.Ei5 = user.id || 0
                invoiceData.Header.Operator = 17
                
                showCheckoutModal.value = true
            }
        }

        // ============= INVOICE METHODS =============
        const closeCheckoutModal = () => {
            showCheckoutModal.value = false
        }

        const updateDecItem = (index, field, value) => {
            invoiceData.Dec[index][field] = value
        }

        const addDecItem = () => {
            if (accountValidDec.value.length > 0) {
                invoiceData.Dec.push({
                    code_hesab: accountValidDec.value[0].code_hesab,
                    Darsad: 0,
                    mablDec: 0
                })
            }
        }

        const removeDecItem = (index) => {
            invoiceData.Dec.splice(index, 1)
        }

        const updateIncItem = (index, field, value) => {
            invoiceData.Inc[index][field] = value
        }

        const addIncItem = () => {
            if (accountValidInc.value.length > 0) {
                invoiceData.Inc.push({
                    code_hesab: accountValidInc.value[0].code_hesab,
                    Darsad: 0,
                    mablInc: 0
                })
            }
        }

        const removeIncItem = (index) => {
            invoiceData.Inc.splice(index, 1)
        }

        const calculateSubTotal = () => {
            return cartItems.value.reduce((total, item) => total + (item.price * item.quantity), 0)
        }

        const calculateTotalDec = () => {
            return invoiceData.Dec.reduce((total, item) => total + (item.mablDec || 0), 0)
        }

        const calculateTotalInc = () => {
            return invoiceData.Inc.reduce((total, item) => total + (item.mablInc || 0), 0)
        }

        const calculateFinalTotal = () => {
            const subTotal = calculateSubTotal()
            const totalDec = calculateTotalDec()
            const totalInc = calculateTotalInc()
            return subTotal - totalDec + totalInc
        }

        // ============= IMAGE ERROR HANDLING =============
        const handleCategoryImageError = (event) => {
            event.target.style.display = 'none'
            const wrapper = event.target.closest('.category-icon-wrapper')
            if (wrapper) {
                const emoji = document.createElement('span')
                emoji.className = 'category-icon-emoji'
                emoji.textContent = '📦'
                wrapper.appendChild(emoji)
            }
        }

        const loadStoreLayout = async () => {
            try {
                const response = await SalesService.getStoreLayout()
                storeLayout.value = response.data?.store_layout === 'large' ? 'large' : 'minimal'
            } catch (error) {
                console.warn('Could not load company store layout; using minimal layout.', error)
            }
        }

        // ============= LIFE CYCLE =============
        onMounted(async () => {
            if (props.isAccountant && (authStore.GetAccountantCustomerId == null || authStore.GetAccountantOperatorId == null)) {
                router.push('/accountant/dashboard')
                return
            }
            if (authStore.isAuthenticated) {
                await loadStoreLayout()
                // Then fetch products, top selling, and category counts in parallel
                await Promise.all([
                    fetchCategories(),
                    fetchProducts(),
                    fetchTopSelling(),
                    fetchCategoryProductCounts()
                ])
                
                isInitialLoad.value = false
            }
        })
        
        return {
            products,
            storeLayout,
            companyName,
            categories,
            topSelling,
            loading,
            searchQuery,
            showFilterPanel,
            showProductModal,
            selectedProduct,
            modalQuantity,
            cartItems,
            currentPage,
            totalProducts,
            totalPages,
            filters,
            activeCategory,
            categoryCounts,
            showCategoryModal,
            showCheckoutModal,
            checkoutLoading,
            invoiceData,
            accountValidDec,
            accountValidInc,
            settingNet,
            groupedProducts,
            isSearchMode,
            cartTotalItems,
            cartTotalAmount,
            visiblePages,
            debouncedSearch,
            handleSearch,
            addToCart,
            addToCartWithQuantity,
            viewProductDetails,
            closeProductModal,
            toggleFilterPanel,
            closeFilterPanel,
            applyFilters,
            resetFilters,
            loadCategoryProducts,
            getProductImage,
            getProductImages,
            getProductImageIndex,
            stepProductImage,
            modalImageIndex,
            getModalImage,
            stepModalImage,
            formatCurrency,
            numberToPersianCurrency,
            prevPage,
            nextPage,
            goToPage,
            isInCart,
            openCategoryModal,
            closeCategoryModal,
            selectCategoryFromModal,
            submitInvoice,
            closeCheckoutModal,
            updateDecItem,
            addDecItem,
            removeDecItem,
            updateIncItem,
            addIncItem,
            removeIncItem,
            calculateSubTotal,
            calculateTotalDec,
            calculateTotalInc,
            calculateFinalTotal,
            goBack,
            goToCategoryPage,
            getProductCountInCategory,
            shoppingCart,
            getProductUnit,
            getCartItemQuantity,
            incrementQuantity,
            decrementQuantity,
            removeFromCart,
            updateQuantityInput,
            getCartItemNote,
            updateNoteInput,
            handleCartUpdate,
            handleCheckout,
            handleCategoryImageError,
            getImageSrc,
            getCategoryFallbackIcon,
                showInvoiceConfirmationModal,
    invoiceConfirmationLoading,
    invoiceConfirmationData,
    invoiceConfirmationError,
    termsAccepted,
    showInvoiceConfirmation,
    closeInvoiceConfirmationModal,
    confirmInvoice,
    onTermsChange,
        }
    }
}
</script>

<style scoped>
.body {
    background: var(--bg);
    min-height: 100vh;
    margin-bottom: 40px;
}

.categories-section {
    background: var(--card-bg);
    border-radius: 16px;
    padding: 16px 20px;
    margin-bottom: 24px;
    box-shadow: 0 2px 12px var(--shadow-1);
    border: 1px solid var(--card-border);
}

.section-header-cat {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: var(--heading);
        margin: 0;
    }

    .view-all-categories {
        background: none;
        border: none;
        color: var(--primary);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px 8px;
        border-radius: 8px;
        transition: all 0.3s ease;

        &:hover {
            background: var(--ring);
        }

        .arrow-icon {
            font-size: 18px;
            line-height: 1;
        }
    }
}

.categories-scroll {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    padding: 4px 0 8px;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
        height: 4px;
    }

    &::-webkit-scrollbar-track {
        background: var(--bg);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: var(--muted-2);
        border-radius: 4px;
    }
}

.category-item {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: var(--bg);
    border-radius: 24px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px solid transparent;
    white-space: nowrap;
    color: var(--text);

    &:hover {
        background: var(--ring);
        border-color: var(--primary);
        transform: translateY(-2px);
    }

    &.active {
        background: var(--primary);
        color: white;
        border-color: var(--primary);

        .category-icon {
            filter: brightness(0) invert(1);
        }
    }

    .category-icon {
        font-size: 18px;
    }

    .category-name {
        font-size: 14px;
        font-weight: 500;
    }
}

.category-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--modal-overlay);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    animation: fadeIn 0.3s ease;
}

.category-modal-content {
    background: var(--card-bg);
    border-radius: 20px;
    max-width: 600px;
    width: 90%;
    max-height: 80vh;
    overflow: hidden;
    animation: slideUp 0.3s ease;
    border: 1px solid var(--card-border);
}

.category-modal-header {
    padding: 20px 24px;
    border-bottom: 1px solid var(--card-border);
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
        font-size: 18px;
        font-weight: 600;
        color: var(--heading);
        margin: 0;
    }

    .close-modal-btn {
        background: none;
        border: none;
        font-size: 28px;
        color: var(--subtext);
        cursor: pointer;
        padding: 0 8px;
        transition: all 0.3s ease;

        &:hover {
            color: var(--heading);
            transform: rotate(90deg);
        }
    }
}

.category-modal-grid {
    padding: 20px 24px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
    max-height: 400px;
    overflow-y: auto;
}

.category-modal-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    background: var(--bg);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: var(--ring);
        transform: translateY(-2px);
    }

    .category-modal-icon {
        font-size: 20px;
    }

    .category-modal-name {
        flex: 1;
        font-size: 14px;
        font-weight: 500;
        color: var(--heading);
    }

    .category-modal-count {
        font-size: 12px;
        color: var(--subtext);
        background: var(--card-border);
        padding: 2px 8px;
        border-radius: 12px;
    }
}

.products-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.product-category-group {
    background: var(--card-bg);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 2px 8px var(--shadow-1);
    border: 1px solid var(--card-border);
}

.category-divider {
    padding: 12px 20px;
    background: var(--bg);
    border-bottom: 2px solid var(--card-border);
    display: flex;
    justify-content: space-between;
    align-items: center;

    .category-label {
        font-size: 16px;
        font-weight: 600;
        color: var(--heading);
        position: relative;
        padding-right: 16px;

        &::before {
            content: '';
            position: absolute;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 4px;
            height: 20px;
            background: var(--primary);
            border-radius: 4px;
        }
    }

    .category-count {
        font-size: 13px;
        color: var(--subtext);
        background: var(--card-border);
        padding: 2px 12px;
        border-radius: 12px;
    }
}

.product-list-item {
    padding: 12px 20px;
    border-bottom: 1px solid var(--card-border);
    transition: all 0.3s ease;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: var(--ring);
    }
}

.product-content {
    display: flex;
    align-items: center;
    gap: 16px;
    direction: ltr;
}

.product-details-left {
    flex: 1;
    min-width: 0;
}

.product-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--heading);
    margin-bottom: 4px;
}

.product-description {
    font-size: 13px;
    color: var(--subtext);
    margin-bottom: 4px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.product-meta-row {
    display: flex;
    gap: 16px;
    font-size: 12px;
    color: var(--muted);
    flex-wrap: wrap;
}

.product-meta-row span {
    display: flex;
    align-items: center;
    gap: 4px;
}

.product-actions-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    min-width: 140px;
    direction: rtl;
}

.product-price {
    font-size: 16px;
    font-weight: 700;
    color: var(--green);
}

.product-image-right {
    position: relative;
    width: 80px;
    height: 80px;
    flex-shrink: 0;
    border-radius: 12px;
    overflow: hidden;
    background: var(--bg);
    border: 1px solid var(--card-border);

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.product-gallery-frame {
    position: relative;

    .gallery-arrow {
        position: absolute;
        z-index: 2;
        top: 50%;
        transform: translateY(-50%);
        width: 30px;
        height: 30px;
        border: 0;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.55);
        color: #fff;
        font-size: 22px;
        line-height: 1;
        cursor: pointer;
    }

    .gallery-prev { left: 7px; }
    .gallery-next { right: 7px; }

    .gallery-count {
        position: absolute;
        z-index: 2;
        bottom: 7px;
        left: 50%;
        transform: translateX(-50%);
        padding: 2px 8px;
        border-radius: 12px;
        background: rgba(0, 0, 0, 0.58);
        color: #fff;
        font-size: 11px;
        line-height: 1.5;
        direction: ltr;
    }
}

.add-to-cart-btn {
    padding: 6px 16px;
    background: var(--primary);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
        background: var(--primary-hover);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px var(--ring);
    }

    &:disabled {
        background: var(--muted);
        cursor: not-allowed;
        opacity: 0.7;
    }
}

.store-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    background: var(--card-bg);
    padding: 20px 24px;
    border-radius: 16px;
    margin-bottom: 24px;
    margin-top: 24px;
    box-shadow: 0 2px 12px var(--shadow-1);
    border: 1px solid var(--card-border);

    .store-title {
        h1 {
            font-size: 24px;
            font-weight: 700;
            color: var(--heading);
            margin: 0;
        }

        p {
            font-size: 14px;
            color: var(--subtext);
            margin: 4px 0 0;
        }
    }

    .store-actions {
        display: flex;
        gap: 12px;
        align-items: center;
        flex-wrap: wrap;

        .search-container {
            position: relative;

            .search-input {
                padding: 10px 16px 10px 40px;
                border: 2px solid var(--card-border);
                border-radius: 12px;
                font-size: 14px;
                min-width: 250px;
                transition: all 0.3s ease;
                background: var(--bg);
                color: var(--text);

                &::placeholder {
                    color: var(--muted);
                }

                &:focus {
                    outline: none;
                    border-color: var(--primary);
                    box-shadow: 0 0 0 4px var(--ring);
                    background: var(--card-bg);
                }
            }

            .search-icon {
                position: absolute;
                right: 12px;
                top: 50%;
                transform: translateY(-50%);
                font-size: 16px;
                color: var(--subtext);
            }
        }

        .filter-btn {
            padding: 10px 20px;
            background: var(--bg);
            border: 2px solid var(--card-border);
            border-radius: 12px;
            font-size: 14px;
            font-weight: 500;
            color: var(--heading);
            cursor: pointer;
            transition: all 0.3s ease;

            &:hover {
                background: var(--ring);
                border-color: var(--primary);
            }
        }
    }
}

.filter-panel {
    background: var(--card-bg);
    border-radius: 16px;
    padding: 20px 24px;
    margin-bottom: 24px;
    box-shadow: 0 4px 16px var(--shadow-1);
    border: 1px solid var(--card-border);
    animation: slideDown 0.3s ease;

    .filter-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;

        h4 {
            font-size: 16px;
            font-weight: 600;
            color: var(--heading);
            margin: 0;
        }

        .close-filter {
            background: none;
            border: none;
            font-size: 24px;
            color: var(--subtext);
            cursor: pointer;

            &:hover {
                color: var(--heading);
            }
        }
    }

    .filter-content {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        align-items: flex-end;

        .filter-section {
            flex: 1;
            min-width: 180px;

            label {
                display: block;
                font-size: 13px;
                font-weight: 500;
                color: var(--heading);
                margin-bottom: 4px;
            }

            .price-range {
                display: flex;
                gap: 8px;
                align-items: center;

                .price-input {
                    flex: 1;
                    padding: 8px 12px;
                    border: 2px solid var(--card-border);
                    border-radius: 8px;
                    font-size: 14px;
                    min-width: 80px;
                    background: var(--bg);
                    color: var(--text);

                    &:focus {
                        outline: none;
                        border-color: var(--primary);
                    }
                }
            }

            .category-select {
                width: 100%;
                padding: 8px 12px;
                border: 2px solid var(--card-border);
                border-radius: 8px;
                font-size: 14px;
                background: var(--bg);
                color: var(--text);

                &:focus {
                    outline: none;
                    border-color: var(--primary);
                }
            }

            .checkbox-label {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 14px;
                color: var(--text);
                cursor: pointer;

                input[type="checkbox"] {
                    width: 18px;
                    height: 18px;
                    cursor: pointer;
                    accent-color: var(--primary);
                }
            }
        }

        .filter-actions {
            display: flex;
            gap: 8px;

            button {
                padding: 8px 20px;
                border: none;
                border-radius: 8px;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.3s ease;

                &.apply-filters {
                    background: var(--primary);
                    color: white;

                    &:hover {
                        background: var(--primary-hover);
                        transform: translateY(-2px);
                        box-shadow: 0 4px 12px var(--ring);
                    }
                }

                &.reset-filters {
                    background: var(--bg);
                    color: var(--subtext);
                    border: 1px solid var(--card-border);

                    &:hover {
                        background: var(--hover);
                    }
                }
            }
        }
    }
}

.products-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 20px;
}

.product-card {
    background: var(--card-bg);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 2px 8px var(--shadow-1);
    transition: all 0.3s ease;
    border: 1px solid var(--card-border);

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px var(--shadow-2);
    }

    .product-image {
        position: relative;
        height: 160px;
        background: var(--bg);
        overflow: hidden;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        .stock-badge {
            position: absolute;
            top: 8px;
            left: 8px;
            background: var(--green);
            color: white;
            padding: 2px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 500;
        }
    }

    .product-info {
        padding: 12px 16px;

        .product-name {
            font-size: 14px;
            font-weight: 600;
            color: var(--heading);
            margin-bottom: 4px;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        .product-price {
            font-size: 16px;
            font-weight: 700;
            color: var(--green);
            margin: 4px 0;
        }

        .product-stock {
            font-size: 12px;
            color: var(--subtext);

            &.low-stock {
                color: var(--orange-1);
            }
        }

        .add-to-cart-btn {
            width: 100%;
            padding: 8px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;

            &:hover:not(:disabled) {
                background: var(--primary-hover);
                transform: translateY(-2px);
                box-shadow: 0 4px 12px var(--ring);
            }

            &:disabled {
                background: var(--muted);
                cursor: not-allowed;
                opacity: 0.7;
            }
        }
    }
}

.section-title1 {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h3 {
        font-size: 18px;
        font-weight: 600;
        color: var(--heading);
        margin: 0;
    }

    .products-count {
        font-size: 14px;
        color: var(--subtext);
        background: var(--bg);
        padding: 4px 12px;
        border-radius: 20px;
    }
}

.products-section {
    background: var(--card-bg);
    border-radius: 16px;
    padding: 20px 24px;
    margin-bottom: 24px;
    box-shadow: 0 2px 12px var(--shadow-1);
    border: 1px solid var(--card-border);
}

.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 40px 0;
    color: var(--subtext);

    .spinner {
        width: 30px;
        height: 30px;
        border: 3px solid var(--card-border);
        border-top-color: var(--primary);
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
    }
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.empty-products {
    text-align: center;
    padding: 40px 0;
    color: var(--subtext);
    font-size: 16px;
}

.pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid var(--card-border);

    .pagination-btn {
        padding: 8px 16px;
        background: var(--bg);
        border: 2px solid var(--card-border);
        border-radius: 8px;
        font-size: 14px;
        color: var(--text);
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover:not(:disabled) {
            background: var(--ring);
            border-color: var(--primary);
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    .page-numbers {
        display: flex;
        gap: 4px;

        .page-number {
            padding: 8px 14px;
            border-radius: 8px;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.3s ease;
            color: var(--subtext);

            &:hover:not(.disabled) {
                background: var(--bg);
            }

            &.active {
                background: var(--primary);
                color: white;
                font-weight: 600;
            }

            &.disabled {
                cursor: default;
                opacity: 0.5;
            }
        }
    }
}

.product-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--modal-overlay);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    animation: fadeIn 0.3s ease;
}

.product-modal-content {
    background: var(--card-bg);
    border-radius: 20px;
    max-width: 800px;
    width: 90%;
    max-height: 90vh;
    overflow: hidden;
    animation: slideUp 0.3s ease;
    border: 1px solid var(--card-border);
}

.product-modal-header {
  
    border-bottom: 1px solid var(--card-border);
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
        font-size: 18px;
        font-weight: 600;
        color: var(--heading);
        margin: 0;
    }

    .close-modal-btn {
        background: none;
        border: none;
        font-size: 28px;
        color: var(--subtext);
        margin-right: auto;
        cursor: pointer;
        padding: 0 8px;
        transition: all 0.3s ease;

        &:hover {
            color: var(--heading);
            transform: rotate(90deg);
        }
    }
}

.product-modal-body {
    display: flex;
    padding: 24px;
    gap: 24px;
    overflow-y: auto;
    max-height: calc(90vh - 80px);

    .product-modal-image {
        flex: 0 0 300px;
        height: 300px;
        border-radius: 12px;
        overflow: hidden;
        background: var(--bg);
        border: 1px solid var(--card-border);

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }

    .product-modal-info {
        flex: 1;

        h2 {
            font-size: 22px;
            font-weight: 700;
            color: var(--heading);
            margin: 0 0 12px;
        }

        .product-modal-price {
            font-size: 20px;
            font-weight: 700;
            color: var(--green);
            margin-bottom: 12px;
        }

        .product-modal-stock {
            padding: 8px 16px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            margin-bottom: 16px;

            &.in-stock {
                background: rgba(16, 185, 129, 0.1);
                color: var(--green);
            }

            &.out-of-stock {
                background: rgba(249, 115, 22, 0.1);
                color: var(--orange-1);
            }
        }

        .product-modal-details {
            background: var(--bg);
            border-radius: 12px;
            padding: 16px;
            margin-bottom: 16px;
            border: 1px solid var(--card-border);

            .detail-row {
                display: flex;
                justify-content: space-between;
                padding: 6px 0;
                /* border-bottom: 1px solid var(--card-border); */

                &:last-child {
                    border-bottom: none;
                }

                .detail-label {
                    font-size: 14px;
                    color: var(--subtext);
                }

                .detail-value {
                    font-size: 14px;
                    font-weight: 500;
                    color: var(--heading);
                }
            }
        }

        .product-modal-description {
            margin-bottom: 16px;

            h4 {
                font-size: 14px;
                font-weight: 600;
                color: var(--heading);
                margin: 0 0 8px;
            }

            p {
                font-size: 14px;
                color: var(--text);
                line-height: 1.6;
                margin: 0;
            }
        }

        .add-to-cart-section {
            display: flex;
            align-items: center;
            gap: 16px;
            margin-top: 16px;

            .quantity-controls {
                display: flex;
                align-items: center;
                gap: 8px;

                .qty-btn {
                    width: 36px;
                    height: 36px;
                    border: 2px solid var(--card-border);
                    border-radius: 8px;
                    background: var(--card-bg);
                    font-size: 18px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    color: var(--text);

                    &:hover {
                        background: var(--bg);
                        border-color: var(--primary);
                    }
                }

                .qty-input {
                    width: 60px;
                    height: 36px;
                    text-align: center;
                    border: 2px solid var(--card-border);
                    border-radius: 8px;
                    font-size: 16px;
                    background: var(--bg);
                    color: var(--text);

                    &:focus {
                        outline: none;
                        border-color: var(--primary);
                    }
                }
            }

            .add-to-cart-modal-btn {
                flex: 1;
                padding: 10px 24px;
                background: var(--primary);
                color: white;
                border: none;
                border-radius: 12px;
                font-size: 16px;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.3s ease;

                &:hover {
                    background: var(--primary-hover);
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px var(--ring);
                }
            }
        }
    }
}

.checkout-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--modal-overlay);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    animation: fadeIn 0.3s ease;
}

.checkout-modal-content {
    background: var(--card-bg);
    border-radius: 20px;
    max-width: 700px;
    width: 90%;
    max-height: 90vh;
    overflow: hidden;
    animation: slideUp 0.3s ease;
    border: 1px solid var(--card-border);
}

.checkout-modal-header {
    padding: 20px 24px;
    border-bottom: 1px solid var(--card-border);
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
        font-size: 18px;
        font-weight: 600;
        color: var(--heading);
        margin: 0;
    }

    .close-checkout-btn {
        background: none;
        border: none;
        font-size: 28px;
        color: var(--subtext);
        cursor: pointer;
        padding: 0 8px;
        transition: all 0.3s ease;

        &:hover {
            color: var(--heading);
            transform: rotate(90deg);
        }
    }
}

.checkout-modal-body {
    padding: 24px;
    overflow-y: auto;
    max-height: calc(90vh - 140px);

    h4 {
        font-size: 16px;
        font-weight: 600;
        color: var(--heading);
        margin: 0 0 12px;
    }

    .invoice-summary, .cart-review, .deductions-section, .additions-section, .comments-section {
        margin-bottom: 24px;

        .summary-card {
            background: var(--bg);
            border-radius: 12px;
            padding: 16px;
            border: 1px solid var(--card-border);

            .summary-row {
                display: flex;
                justify-content: space-between;
                padding: 8px 0;
                border-bottom: 1px solid var(--card-border);

                &:last-child {
                    border-bottom: none;
                }

                .amount {
                    font-weight: 600;
                    color: var(--green);
                }
            }
        }
    }

    .cart-review-items {
        max-height: 200px;
        overflow-y: auto;

        .cart-review-item {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid var(--card-border);

            .item-info {
                display: flex;
                gap: 16px;

                .item-name {
                    font-weight: 500;
                    color: var(--heading);
                }

                .item-quantity {
                    color: var(--subtext);
                }
            }

            .item-total {
                font-weight: 600;
                color: var(--green);
            }
        }
    }

    .deductions-section, .additions-section {
        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;

            .add-item-btn {
                padding: 6px 16px;
                background: var(--primary);
                color: white;
                border: none;
                border-radius: 8px;
                font-size: 13px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.3s ease;

                &:hover {
                    background: var(--primary-hover);
                }
            }
        }

        .empty-section {
            padding: 16px;
            text-align: center;
            color: var(--subtext);
            background: var(--bg);
            border-radius: 8px;
        }

        .deduction-item, .addition-item {
            .item-row {
                display: flex;
                gap: 8px;
                align-items: center;
                margin-bottom: 8px;

                .account-select {
                    flex: 2;
                    padding: 8px;
                    border: 2px solid var(--card-border);
                    border-radius: 8px;
                    font-size: 14px;
                    background: var(--bg);
                    color: var(--text);

                    &:focus {
                        outline: none;
                        border-color: var(--primary);
                    }
                }

                .percent-input, .amount-input {
                    flex: 1;
                    padding: 8px;
                    border: 2px solid var(--card-border);
                    border-radius: 8px;
                    font-size: 14px;
                    min-width: 80px;
                    background: var(--bg);
                    color: var(--text);

                    &:focus {
                        outline: none;
                        border-color: var(--primary);
                    }
                }

                .remove-item-btn {
                    padding: 6px 10px;
                    background: rgba(249, 115, 22, 0.1);
                    color: var(--orange-1);
                    border: none;
                    border-radius: 8px;
                    font-size: 16px;
                    cursor: pointer;
                    transition: all 0.3s ease;

                    &:hover {
                        background: rgba(249, 115, 22, 0.2);
                    }
                }
            }
        }

        .total-row {
            display: flex;
            justify-content: flex-end;
            gap: 16px;
            padding: 8px 0;
            font-weight: 600;

            .amount {
                &.negative {
                    color: var(--orange-1);
                }

                &.positive {
                    color: var(--green);
                }
            }
        }
    }

    .final-total {
        padding: 16px;
        background: var(--bg);
        border-radius: 12px;
        border: 2px solid var(--card-border);

        .total-row.large {
            font-size: 18px;

            .amount.final {
                font-size: 22px;
                color: var(--green);
            }
        }
    }

    .comments-section {
        label {
            display: block;
            font-size: 14px;
            font-weight: 500;
            color: var(--heading);
            margin-bottom: 4px;
        }

        .comments-input {
            width: 100%;
            padding: 12px;
            border: 2px solid var(--card-border);
            border-radius: 12px;
            font-size: 14px;
            font-family: inherit;
            resize: vertical;
            background: var(--bg);
            color: var(--text);

            &:focus {
                outline: none;
                border-color: var(--primary);
            }
        }
    }
}

.checkout-modal-footer {
    padding: 16px 24px;
    border-top: 1px solid var(--card-border);
    display: flex;
    justify-content: flex-end;
    gap: 12px;

    .cancel-btn {
        padding: 10px 24px;
        background: var(--bg);
        color: var(--subtext);
        border: 1px solid var(--card-border);
        border-radius: 12px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover:not(:disabled) {
            background: var(--hover);
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    .submit-invoice-btn {
        padding: 10px 32px;
        background: var(--primary);
        color: white;
        border: none;
        border-radius: 12px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover:not(:disabled) {
            background: var(--primary-hover);
            transform: translateY(-2px);
            box-shadow: 0 4px 12px var(--ring);
        }

        &:disabled {
            opacity: 0.7;
            cursor: not-allowed;
        }

        .spinner-small {
            display: inline-block;
            width: 16px;
            height: 16px;
            border: 2px solid rgba(255,255,255,0.3);
            border-top-color: white;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
        }
    }
}

/* Cart Controls */
.cart-controls {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
}

.qty-control-btn {
    width: 28px;
    height: 28px;
    border: 2px solid var(--card-border);
    border-radius: 6px;
    background: var(--card-bg);
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text);
}

.qty-control-btn:hover:not(:disabled) {
    background: var(--bg);
    border-color: var(--primary);
}

.qty-control-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.qty-control-btn.decrement {
    color: var(--orange-1);
}

.qty-control-btn.decrement:hover:not(:disabled) {
    background: rgba(249, 115, 22, 0.1);
    border-color: var(--orange-1);
}

.qty-control-btn.increment {
    color: var(--green);
}

.qty-control-btn.increment:hover:not(:disabled) {
    background: rgba(16, 185, 129, 0.1);
    border-color: var(--green);
}

.cart-quantity {
    min-width: 24px;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: var(--heading);
}

.remove-from-cart-btn {
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 50%;
    background: rgba(249, 115, 22, 0.1);
    color: var(--orange-1);
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    line-height: 1;
}

.remove-from-cart-btn:hover {
    background: rgba(249, 115, 22, 0.2);
    transform: scale(1.1);
}

.product-stock-info {
    font-size: 12px;
    color: var(--subtext);
}

.product-stock-info.low-stock {
    color: var(--orange-1);
    font-weight: 600;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Responsive */
@media (max-width: 768px) {
    .store-header {
        flex-direction: column;
        align-items: stretch;

        .store-actions {
            flex-direction: column;

            .search-container {
                .search-input {
                    min-width: 100%;
                }
            }

            .filter-btn {
                width: 100%;
            }
        }
    }

    .filter-panel .filter-content {
        flex-direction: column;

        .filter-section {
            min-width: 100%;
        }

        .filter-actions {
            width: 100%;
            flex-direction: column;

            button {
                width: 100%;
            }
        }
    }

    .product-modal-body {
        flex-direction: column;

        .product-modal-image {
            flex: 0 0 200px;
            height: 200px;
        }
    }

    .product-content {
        flex-wrap: wrap;
    }

    .product-actions-right {
        min-width: 100%;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        order: 3;
    }

    .product-image-right {
        width: 60px;
        height: 60px;
        order: 2;
    }

    .product-details-left {
        order: 1;
    }

    .category-modal-grid {
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    }

    .cart-controls {
        flex-wrap: wrap;
        justify-content: flex-start;
    }
    
    .qty-control-btn {
        width: 32px;
        height: 32px;
        font-size: 18px;
    }
    
    .cart-quantity {
        min-width: 28px;
        font-size: 16px;
    }
}

/* Product Modal - Compact & Professional */
.product-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3000;
    animation: fadeIn 0.3s ease;
}

.product-modal-content {
    background: var(--bg);
    border-radius: 16px;
    width: 100%;
    max-width: 750px;
    max-height: 85vh;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: slideUp 0.3s ease;
}

.product-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #f3f4f6;
    background: var(--card-bg);
}

.product-modal-header h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: var(--text);
}

.close-modal-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #9ca3af;
    transition: all 0.3s ease;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-modal-btn:hover {
    background: #f3f4f6;
    color: #1a1a2e;
    transform: rotate(90deg);
}

.product-modal-body {
    display: flex;
    gap: 24px;
    padding: 24px;
    overflow-y: auto;
    max-height: calc(85vh - 70px);
}

.product-modal-image {
    flex: 0 0 200px;
    height: 200px;
    border-radius: 12px;
    overflow: hidden;
    background: #f8f9fa;
    border: 1px solid #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
}

.product-modal-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.product-modal-info {
    flex: 1;
    min-width: 0;
}

.product-modal-info h2 {
    font-size: 20px;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0 0 8px 0;
    line-height: 1.3;
}

.product-modal-price {
    font-size: 22px;
    font-weight: 700;
    color: #059669;
    margin-bottom: 10px;
}

.product-modal-stock {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 14px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 14px;
}

.product-modal-stock.in-stock {
    background: #ecfdf5;
    color: #059669;
}

.product-modal-stock.out-of-stock {
    background: #fef2f2;
    color: #dc2626;
}

.stock-icon {
    font-weight: 700;
    font-size: 14px;
}

.product-modal-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px 16px;
    background: #f8f9fa;
    border-radius: 10px;
    padding: 12px 16px;
    margin-bottom: 12px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    /* border-bottom: 1px solid #f3f4f6; */
}

.detail-row:last-child {
    border-bottom: none;
}

.detail-label {
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
}

.detail-value {
    font-size: 13px;
    font-weight: 600;
    color: #1a1a2e;
}

.product-modal-description {
    background: #f8f9fa;
    border-radius: 10px;
    padding: 12px 16px;
    margin-bottom: 14px;
}

.product-modal-description p {
    font-size: 13px;
    color: #4b5563;
    line-height: 1.6;
    margin: 0;
}

/* Add to Cart Section */
.add-to-cart-section {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-top: 14px;
    border-top: 1px solid #f3f4f6;
}

.quantity-controls {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.qty-btn {
    width: 34px;
    height: 34px;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1a1a2e;
}

.qty-btn:hover {
    background: #f3f4f6;
    border-color: #4a6cf7;
}

.qty-btn.minus:hover {
    background: #fef2f2;
    border-color: #dc2626;
    color: #dc2626;
}

.qty-btn.plus:hover {
    background: #ecfdf5;
    border-color: #059669;
    color: #059669;
}

.qty-input {
    width: 50px;
    height: 34px;
    text-align: center;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    background: white;
    color: #1a1a2e;
}

.qty-input:focus {
    outline: none;
    border-color: #4a6cf7;
    box-shadow: 0 0 0 3px rgba(74, 108, 247, 0.1);
}

.add-to-cart-modal-btn {
    flex: 1;
    padding: 10px 20px;
    background: #4a6cf7;
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    white-space: nowrap;
}

.add-to-cart-modal-btn:hover {
    background: #3b5de7;
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(74, 108, 247, 0.3);
}

.add-to-cart-modal-btn:active {
    transform: translateY(0);
}

/* Animations */
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

/* Responsive */
@media (max-width: 768px) {
    .product-modal-content {
        max-width: 98%;
        max-height: 90vh;
    }
    
    .product-modal-body {
        flex-direction: column;
        gap: 16px;
        padding: 16px;
    }
    
    .product-modal-image {
        flex: 0 0 160px;
        height: 160px;
        width: 100%;
    }
    
    .product-modal-info h2 {
        font-size: 18px;
    }
    
    .product-modal-price {
        font-size: 20px;
    }
    
    .product-modal-details {
        grid-template-columns: 1fr;
        gap: 2px;
    }
    
    .add-to-cart-section {
        flex-direction: column;
        gap: 10px;
    }
    
    .add-to-cart-modal-btn {
        width: 100%;
    }
    
    .quantity-controls {
        width: 100%;
        justify-content: center;
    }
}
.qty-input-cart {
    width: 50px;
    height: 28px;
    text-align: center;
    border: 2px solid #e5e7eb;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    background: white;
    color: #1a1a2e;
    padding: 0 4px;
}

/* Allow decimal input */
.qty-input-cart[type="number"] {
    -moz-appearance: textfield;
}

.qty-input-cart::-webkit-inner-spin-button,
.qty-input-cart::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* If you want to show the spin buttons for decimal input */
.qty-input-cart.show-spinner::-webkit-inner-spin-button,
.qty-input-cart.show-spinner::-webkit-outer-spin-button {
    -webkit-appearance: inner-spin-button;
    opacity: 1;
}

/* Add to your existing styles */
.cart-note-input {
    display: flex;
    align-items: center;
    margin-top: 6px;
    width: 100%;
    position: relative;
}

.note-input-field {
    width: 100%;
    padding: 4px 30px 4px 8px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    font-size: 12px;
    direction: rtl;
    background: var(--bg);
    color: var(--text);
    transition: all 0.3s ease;
}

.note-input-field:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
    background: var(--bg);
}

.note-input-field::placeholder {
    color: #999;
    font-size: 11px;
}

.note-icon {
    position: absolute;
    left: 8px;
    font-size: 14px;
    opacity: 0.5;
    pointer-events: none;
}

/* Adjust the product actions right layout */
.product-actions-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    min-width: 180px;
}

.cart-controls {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

/* Mobile responsive */
@media (max-width: 768px) {
    .product-actions-right {
        min-width: 140px;
        width: 100%;
    }
    
    .cart-note-input {
        width: 100%;
    }
    
    .note-input-field {
        font-size: 11px;
        padding: 3px 25px 3px 6px;
    }
}
.category-item {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: var(--bg);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px solid transparent;
    min-width: 70px;
    color: var(--text);

    &:hover {
        background: var(--ring);
        border-color: var(--primary);
        transform: translateY(-2px);
    }

    &.active {
        background: var(--primary);
        color: white;
        border-color: var(--primary);

        .category-icon-wrapper {
            background: rgba(255, 255, 255, 0.2);
        }
    }

    .category-icon-wrapper {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        overflow: hidden;
        flex-shrink: 0;
        background: var(--card-bg);
        border: 2px solid var(--card-border);
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        
        .category-icon-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
        
        .category-icon-emoji {
            font-size: 24px;
        }
    }

    .category-name {
        font-size: 12px;
        font-weight: 500;
        text-align: center;
        line-height: 1.2;
        max-width: 70px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}
.category-modal-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 16px 12px;
    background: var(--bg);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: center;

    &:hover {
        background: var(--ring);
        transform: translateY(-4px);
        box-shadow: 0 4px 12px var(--shadow-1);
    }

    .category-modal-icon-wrapper {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        overflow: hidden;
        flex-shrink: 0;
        background: var(--card-bg);
        border: 2px solid var(--card-border);
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        transition: all 0.3s ease;
        
        .category-modal-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
        
        .category-modal-emoji {
            font-size: 32px;
        }
    }

    .category-modal-name {
        flex: 1;
        font-size: 14px;
        font-weight: 500;
        color: var(--heading);
        line-height: 1.3;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        min-height: 36px;
    }

    .category-modal-count {
        font-size: 12px;
        color: var(--subtext);
        background: var(--card-border);
        padding: 2px 10px;
        border-radius: 12px;
        font-weight: 500;
    }
}

.category-modal-grid {
    padding: 20px 24px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 16px;
    max-height: 400px;
    overflow-y: auto;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: var(--bg);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: var(--muted-2);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: var(--muted);
    }
}
/* Invoice Confirmation Modal */
.invoice-confirmation-modal {
    width: 92%;
    max-width: 600px;
    background: var(--card-bg);
    border-radius: 16px;
    padding: 0;
    box-shadow: 0 8px 32px rgba(0,0,0,0.25);
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.invoice-confirmation-modal .modal-body {
    padding: 20px 24px;
    overflow-y: auto;
    flex: 1;
}

.confirmation-summary {
    background: var(--bg);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 20px;
    border: 1px solid var(--border);
}

.confirmation-summary .summary-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid var(--border);
}

.confirmation-summary .summary-row:last-child {
    border-bottom: none;
}

.confirmation-summary .summary-row .label {
    color: var(--text-muted);
    font-size: 14px;
}

.confirmation-summary .summary-row .value {
    color: var(--text);
    font-weight: 500;
    font-size: 14px;
}

.confirmation-summary .summary-row.total .value {
    font-size: 18px;
    font-weight: 700;
    color: var(--green);
}

.confirmation-items {
    margin-bottom: 20px;
}

.confirmation-items h4 {
    margin: 0 0 12px 0;
    font-size: 16px;
    color: var(--heading);
}

.items-list {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid var(--border);
    border-radius: 8px;
}

.items-list .item-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid var(--border);
}

.items-list .item-row:last-child {
    border-bottom: none;
}

.items-list .item-name {
    flex: 1;
    font-size: 14px;
    color: var(--text);
}

.items-list .item-details {
    font-size: 13px;
    color: var(--text-muted);
    margin: 0 12px;
}

.items-list .item-total {
    font-size: 14px;
    font-weight: 600;
    color: var(--green);
}

.terms-section {
    padding: 12px 0;
    border-top: 1px solid var(--border);
}

.terms-checkbox {
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    font-size: 14px;
    color: var(--text);
}

.terms-checkbox input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: var(--primary);
}

.terms-text {
    color: var(--text);
    font-weight: 500;
}

.confirm-btn {
    padding: 10px 24px;
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.confirm-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
}

.confirm-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}

.confirm-btn .spinner-small {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}
.filter-panel {
    background: var(--card-bg);
    border-radius: 16px;
    padding: 20px 24px;
    margin-bottom: 24px;
    box-shadow: 0 4px 16px var(--shadow-1);
    border: 1px solid var(--card-border);
    animation: slideDown 0.3s ease;
    position: relative;
    max-width: 100%;
    overflow: hidden;
    box-sizing: border-box;

    .filter-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;

        h4 {
            font-size: 16px;
            font-weight: 600;
            color: var(--heading);
            margin: 0;
        }

        .close-filter {
            background: none;
            border: none;
            font-size: 24px;
            color: var(--subtext);
            cursor: pointer;
            padding: 0 8px;
            flex-shrink: 0;

            &:hover {
                color: var(--heading);
            }
        }
    }

    .filter-content {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        align-items: flex-end;
        width: 100%;
        box-sizing: border-box;

        .filter-section {
            flex: 1 1 180px;
            min-width: 150px;
            max-width: 100%;

            label {
                display: block;
                font-size: 13px;
                font-weight: 500;
                color: var(--heading);
                margin-bottom: 4px;
            }

            .price-range {
                display: flex;
                gap: 8px;
                align-items: center;
                flex-wrap: wrap;

                .price-input {
                    flex: 1;
                    min-width: 60px;
                    max-width: 120px;
                    padding: 8px 12px;
                    border: 2px solid var(--card-border);
                    border-radius: 8px;
                    font-size: 14px;
                    background: var(--bg);
                    color: var(--text);
                    box-sizing: border-box;

                    &:focus {
                        outline: none;
                        border-color: var(--primary);
                    }
                }

                span {
                    flex-shrink: 0;
                }
            }

            .category-select {
                width: 100%;
                max-width: 100%;
                padding: 8px 12px;
                border: 2px solid var(--card-border);
                border-radius: 8px;
                font-size: 14px;
                background: var(--bg);
                color: var(--text);
                box-sizing: border-box;

                &:focus {
                    outline: none;
                    border-color: var(--primary);
                }
            }

            .checkbox-label {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 14px;
                color: var(--text);
                cursor: pointer;
                white-space: nowrap;

                input[type="checkbox"] {
                    width: 18px;
                    height: 18px;
                    cursor: pointer;
                    accent-color: var(--primary);
                    flex-shrink: 0;
                }
            }
        }

        .filter-actions {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            flex-shrink: 0;

            button {
                padding: 8px 20px;
                border: none;
                border-radius: 8px;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.3s ease;
                white-space: nowrap;

                &.apply-filters {
                    background: var(--primary);
                    color: white;

                    &:hover {
                        background: var(--primary-hover);
                        transform: translateY(-2px);
                        box-shadow: 0 4px 12px var(--ring);
                    }
                }

                &.reset-filters {
                    background: var(--bg);
                    color: var(--subtext);
                    border: 1px solid var(--card-border);

                    &:hover {
                        background: var(--hover);
                    }
                }
            }
        }
    }
}
/* Responsive */
@media (max-width: 768px) {
    .store-header {
        flex-direction: column;
        align-items: stretch;

        .store-actions {
            flex-direction: column;

            .search-container {
                .search-input {
                    min-width: 100%;
                    box-sizing: border-box;
                }
            }

            .filter-btn {
                width: 100%;
            }
        }
    }

    .filter-panel {
        padding: 16px;
        margin: 0 8px 16px 8px;
        width: auto;
        
        .filter-content {
            flex-direction: column;
            gap: 12px;

            .filter-section {
                flex: 1 1 100%;
                min-width: 100%;
                
                .price-range {
                    .price-input {
                        max-width: 100%;
                        min-width: 0;
                    }
                }
            }

            .filter-actions {
                width: 100%;
                flex-direction: column;

                button {
                    width: 100%;
                }
            }
        }
    }

    .product-modal-body {
        flex-direction: column;

        .product-modal-image {
            flex: 0 0 200px;
            height: 200px;
        }
    }

    .product-content {
        flex-wrap: wrap;
    }

    .product-actions-right {
        min-width: 100%;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        order: 3;
    }

    .product-image-right {
        width: 60px;
        height: 60px;
        order: 2;
    }

    .product-details-left {
        order: 1;
    }

    .category-modal-grid {
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    }

    .cart-controls {
        flex-wrap: wrap;
        justify-content: flex-start;
    }
    
    .qty-control-btn {
        width: 32px;
        height: 32px;
        font-size: 18px;
    }
    
    .cart-quantity {
        min-width: 28px;
        font-size: 16px;
    }
}
.store-layout-large .products-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 1fr));
    gap: 18px;
}

.store-layout-large .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr));
}

.store-layout-large .product-card .product-image {
    height: auto;
    aspect-ratio: 1.55 / 1;
}

.store-layout-large .product-category-group {
    display: contents;
}

.store-layout-large .product-list-item {
    padding: 10px;
    border: 1px solid var(--card-border);
    border-radius: 18px;
    background: var(--card-bg);
    box-shadow: 0 2px 10px var(--shadow-1);
}

.store-layout-large .product-content {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
}

.store-layout-large .product-image-right {
    order: 0;
    width: 100%;
    height: auto;
    aspect-ratio: 1.55 / 1;
    border-radius: 14px;
    cursor: pointer;
}

.store-layout-large .product-details-left {
    order: 1;
    padding: 14px 8px 4px;
    text-align: right;
    direction: rtl;
}

.store-layout-large .product-title {
    font-size: 19px;
    line-height: 1.5;
}

.store-layout-large .product-description {
    -webkit-line-clamp: 3;
    line-height: 1.7;
    text-align: right;
}

.store-layout-large .product-meta-row {
    margin-top: 8px;
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    gap: 8px;
    direction: rtl;
    text-align: right;
    overflow-x: auto;
    white-space: nowrap;
}

.store-layout-large .product-meta-row span {
    flex: 0 0 auto;
    white-space: nowrap;
    text-align: right;
}

.store-layout-large .product-actions-right {
    order: 2;
    width: 100%;
    min-width: 0;
    padding: 8px;
    align-items: stretch;
}

.store-layout-large .cart-controls {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: stretch;
    gap: 8px;
}

.store-layout-large .cart-controls .add-to-cart-btn {
    flex: 1;
    min-height: 42px;
}

.store-layout-large .cart-controls.cart-item-controls-open {
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
}

.store-layout-large .cart-controls.cart-item-controls-open .qty-input-cart {
    width: 58px;
    min-width: 48px;
    flex: 0 1 58px;
}

.store-layout-large .cart-controls.cart-item-controls-open .qty-control-btn,
.store-layout-large .cart-controls.cart-item-controls-open .remove-from-cart-btn {
    flex: 0 0 auto;
}

.product-modal-price {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    text-align: right;
    direction: rtl;
}

.product-modal-price-label {
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
}

.product-modal-price-words {
    margin: -6px 0 12px;
    color: #059669;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.8;
    text-align: right;
    direction: rtl;
}

.store-layout-large .product-actions-right .product-price {
    order: -1;
    text-align: right;
    font-size: 19px;
    width: 100%;
}

.store-layout-large .product-actions-right,
.store-layout-large .product-details-left,
.store-layout-large .product-title,
.store-layout-large .product-description {
    text-align: right;
}

.store-layout-large .store-header .store-actions {
    display: flex;
    width: 100%;
    flex-wrap: nowrap;
    direction: ltr;
    gap: 10px;
}

.store-layout-large .store-header .store-actions .search-container {
    flex: 0 0 80%;
    min-width: 0;
}

.store-layout-large .store-header .store-actions .search-container .search-input {
    width: 100%;
    min-width: 0;
    min-height: 48px;
}

.store-layout-large .store-header .store-actions .filter-btn {
    flex: 1 1 20%;
    min-width: 0;
    padding-left: 8px;
    padding-right: 8px;
}

@media (max-width: 600px) {
    .product-list-item .product-actions-right {
        width: 100%;
        min-width: 0;
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }

    .product-list-item .cart-controls {
        width: 100%;
    }

    .product-list-item .cart-controls.cart-item-controls-open {
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        align-items: center;
        gap: 6px;
    }

    .product-list-item .cart-controls.cart-item-controls-open .qty-input-cart {
        width: 52px;
        min-width: 44px;
        flex: 0 1 52px;
    }

    .product-list-item .cart-controls.cart-item-controls-open .qty-control-btn,
    .product-list-item .cart-controls.cart-item-controls-open .remove-from-cart-btn {
        flex: 0 0 auto;
    }

    .store-layout-large .store-header .store-actions {
        flex-direction: row;
    }

    .store-layout-large .store-header .store-actions .search-container {
        flex-basis: 80%;
    }

    .store-layout-large .store-header .store-actions .filter-btn {
        width: auto;
        flex-basis: 20%;
    }

    .store-layout-large .products-list {
        grid-template-columns: 1fr;
        gap: 14px;
    }

    .store-layout-large .products-grid {
        grid-template-columns: 1fr;
    }

    .store-layout-large .product-list-item {
        padding: 9px;
    }

    .store-layout-large .product-image-right {
        aspect-ratio: 1.5 / 1;
    }
}
</style>
